import api from '../index'

// 菜单数据类型定义
export interface Menu {
  id: number
  name: string
  title: string
  path?: string
  component?: string
  icon?: string
  type: 1 | 2 | 3 // 1=目录，2=菜单，3=按钮
  parent_id: number
  level: number
  sort_order: number
  status: 0 | 1 // 0=禁用，1=启用
  permission_code?: string
  is_hidden: 0 | 1 // 0=显示，1=隐藏
  keep_alive: 0 | 1 // 0=不缓存，1=缓存
  external_link?: string
  remark?: string
  created_by: number
  updated_by?: number
  created_at: string
  updated_at: string
  children?: Menu[]
  parent?: Menu
  creator?: {
    id: number
    username: string
    name: string
  }
  updater?: {
    id: number
    username: string
    name: string
  }
}

// 菜单选项类型
export interface MenuOption {
  value: number
  label: string
}

// 菜单搜索参数
export interface MenuSearchParams {
  name?: string
  title?: string
  type?: number
  status?: number
  parent_id?: number
  per_page?: number
  page?: number
}

// 菜单创建/更新参数
export interface MenuFormData {
  name: string
  title: string
  path?: string
  component?: string
  icon?: string
  type: 1 | 2 | 3
  parent_id?: number
  sort_order?: number
  status?: boolean
  permission_code?: string
  is_hidden?: boolean
  keep_alive?: boolean
  external_link?: string
  remark?: string
  permission_ids?: number[]
}

// 批量状态更新参数
export interface BatchStatusParams {
  ids: number[]
  status: 0 | 1
}

// 菜单管理API
export const menuAPI = {
  /**
   * 获取菜单列表
   */
  getList(params?: MenuSearchParams) {
    return api.get('menus', { params })
  },

  /**
   * 获取菜单树形结构
   */
  getTree(params?: { type?: number, status?: number }) {
    return api.get('menus/tree', { params })
  },

  /**
   * 获取菜单选项（用于父级选择）
   */
  getOptions(params?: { exclude_id?: number }) {
    return api.get('menus/options', { params })
  },

  /**
   * 创建菜单
   */
  create(data: MenuFormData) {
    return api.post('menus', data)
  },

  /**
   * 获取菜单详情
   */
  getDetail(id: number) {
    return api.get(`menus/${id}`)
  },

  /**
   * 更新菜单
   */
  update(id: number, data: Partial<MenuFormData>) {
    return api.put(`menus/${id}`, data)
  },

  /**
   * 删除菜单
   */
  delete(id: number) {
    return api.delete(`menus/${id}`)
  },

  /**
   * 批量更新菜单状态
   */
  batchUpdateStatus(data: BatchStatusParams) {
    return api.patch('menus/batch/status', data)
  },
}

// 菜单类型映射
export const MenuTypeMap = {
  1: { text: '目录', color: 'primary' },
  2: { text: '菜单', color: 'success' },
  3: { text: '按钮', color: 'warning' },
} as const

// 菜单状态映射
export const MenuStatusMap = {
  0: { text: '禁用', color: 'danger' },
  1: { text: '启用', color: 'success' },
} as const

// 工具函数
export const menuUtils = {
  /**
   * 获取菜单类型文本
   */
  getTypeText(type: number): string {
    return MenuTypeMap[type as keyof typeof MenuTypeMap]?.text || '未知'
  },

  /**
   * 获取菜单类型颜色
   */
  getTypeColor(type: number): string {
    return MenuTypeMap[type as keyof typeof MenuTypeMap]?.color || 'info'
  },

  /**
   * 获取菜单状态文本
   */
  getStatusText(status: number): string {
    return MenuStatusMap[status as keyof typeof MenuStatusMap]?.text || '未知'
  },

  /**
   * 获取菜单状态颜色
   */
  getStatusColor(status: number): string {
    return MenuStatusMap[status as keyof typeof MenuStatusMap]?.color || 'info'
  },

  /**
   * 构建菜单树选项
   */
  buildTreeOptions(menus: Menu[], excludeId?: number): Array<{ value: number, label: string, children?: any[] }> {
    return menus
      .filter(menu => menu.id !== excludeId && menu.type !== 3) // 排除按钮类型
      .map(menu => ({
        value: menu.id,
        label: menu.title,
        children: menu.children ? this.buildTreeOptions(menu.children, excludeId) : undefined,
      }))
  },

  /**
   * 获取所有菜单ID（用于展开/收起）
   */
  getAllMenuIds(menus: Menu[]): number[] {
    let ids: number[] = []
    menus.forEach((menu) => {
      if (menu.children && menu.children.length > 0) {
        ids.push(menu.id)
        ids = ids.concat(this.getAllMenuIds(menu.children))
      }
    })
    return ids
  },

  /**
   * 验证菜单路径格式
   */
  validatePath(path: string): boolean {
    return /^\/[\w/-]*$/.test(path)
  },

  /**
   * 验证权限代码格式
   */
  validatePermissionCode(code: string): boolean {
    return /^[\w:-]+$/.test(code)
  },
}

export default menuAPI
